import { AlignCenterIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/align-center-icon';
import { AlignLeftIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/align-left-icon';
import { AlignRightIcon } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-icons/align-right-icon';
import { getTable } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/lib/tiptap-table-utils';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import { isExtensionAvailable } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import type { Editor } from '@tiptap/react';
import { useCallback } from 'react';

export type TableAlignment = 'left' | 'center' | 'right';

export interface UseTableAlignConfig {
  editor?: Editor | null;
  hideWhenUnavailable?: boolean;
}

const REQUIRED_EXTENSIONS = ['table', 'tableHandleExtension'];

const ALIGNMENT_ICONS: Record<TableAlignment, React.ComponentType<{ className?: string }>> = {
  left: AlignLeftIcon,
  center: AlignCenterIcon,
  right: AlignRightIcon,
};

const ALIGNMENT_LABELS: Record<TableAlignment, string> = {
  left: 'Left',
  center: 'Center',
  right: 'Right',
};

function getTableAlign(editor: Editor | null): TableAlignment {
  if (!editor) return 'left';

  try {
    const table = getTable(editor);
    if (!table) return 'left';

    return (table.node.attrs['tableAlign'] as TableAlignment) ?? 'left';
  } catch {
    return 'left';
  }
}

function canSetTableAlign(editor: Editor | null): boolean {
  if (!editor || !editor.isEditable || !isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) {
    return false;
  }

  try {
    return editor.isActive('table') || editor.isActive('tableCell') || editor.isActive('tableHeader');
  } catch {
    return false;
  }
}

function setTableAlign({ editor, alignment }: { editor: Editor | null; alignment: TableAlignment }): boolean {
  if (!canSetTableAlign(editor) || !editor) {
    return false;
  }

  try {
    const table = getTable(editor);
    if (!table) return false;

    const tr = editor.state.tr;
    tr.setNodeMarkup(table.pos, undefined, {
      ...table.node.attrs,
      tableAlign: alignment,
    });

    if (tr.docChanged) {
      editor.view.dispatch(tr);
    }

    return true;
  } catch (error) {
    console.error('Error setting table alignment:', error);
    return false;
  }
}

export function useTableAlign(config: UseTableAlignConfig = {}) {
  const { editor: providedEditor, hideWhenUnavailable = false } = config;

  const { editor } = useTiptapEditor(providedEditor);

  const canAlign = canSetTableAlign(editor);
  const currentAlignment = getTableAlign(editor);

  const isVisible = (() => {
    if (!editor || !editor.isEditable) return false;
    if (!isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) return false;
    return hideWhenUnavailable ? canAlign : true;
  })();

  const handleAlign = useCallback(
    (alignment: TableAlignment) => {
      return setTableAlign({ editor, alignment });
    },
    [editor],
  );

  return {
    isVisible,
    canAlign,
    handleAlign,
    currentAlignment,
    getIcon: (alignment: TableAlignment) => ALIGNMENT_ICONS[alignment],
    getLabel: (alignment: TableAlignment) => ALIGNMENT_LABELS[alignment],
  };
}
